'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Icon, Spinner } from '@/src/components/ui';
import { Input } from '@workspace/ui/components/input';
import { cn } from '@workspace/ui/lib/utils';
import {
  ensureIgdbGameInDb,
  searchSocialGames,
} from '@/src/actions/social-posts';
import type { SearchGameItem } from 'types/src/types';
import { noBorderStyles } from '@/src/constants/constants';
import { useBoolean } from '@/src/hooks/use-boolean';
import { useResponsive } from '@/src/hooks/use-responsive';

type SearchBarProps = {
  query: string;
  setQuery: (v: string) => void;
  onFocusOpen: () => void;
  onEscapeClose: () => void;
  onClear: () => void;
  onIconClick?: () => void;
  isMobile: boolean;
};

function SearchBar({
  query,
  setQuery,
  onFocusOpen,
  onEscapeClose,
  onClear,
  onIconClick,
  isMobile,
}: SearchBarProps) {
  return (
    <div
      className={cn(
        'flex items-center relative font-grotesk justify-center xl:min-w-110 min-w-[350px] border border-black/20 rounded-sm overflow-hidden md:h-10 h-8 bg-white',
        {
          ' border-black': query,
        },
      )}
    >
      <button
        type="button"
        className="bg-[#F8F8F8] md:p-2 p-1 border-r md:h-10 h-8"
        onClick={onIconClick ?? onFocusOpen}
      >
        <Icon
          name="magnifying-glass"
          className="md:size-6 size-5 text-[#929292]"
        />
      </button>

      <Input
        style={noBorderStyles}
        placeholder={
          !isMobile
            ? 'Explore gaming’s home for creators & players...'
            : 'Explore gaming’s home for players'
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={onFocusOpen}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onEscapeClose();
          if (e.key === 'Enter') onFocusOpen();
        }}
        className="md:h-10 h-8"
      />

      {query && (
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2"
          onClick={onClear}
        >
          <Icon
            className="size-3 text-black/80 hover:cursor-pointer"
            name="close"
          />
        </button>
      )}
    </div>
  );
}

type DropdownProps = {
  open: boolean;
  loading: boolean;
  query: string;
  items: SearchGameItem[];
  hoveredKey: string | null;
  setHoveredKey: (k: string | null) => void;
  selectedItem: SearchGameItem | null;
  onSelect: (item: SearchGameItem) => void;
};

function DropdownResults({
  open,
  loading,
  query,
  items,
  hoveredKey,
  setHoveredKey,
  selectedItem,
  onSelect,
}: DropdownProps) {
  if (!open) return null;

  const q = query.trim();
  const showEmpty = !loading && q.length > 0 && items.length === 0;
  const showList = !loading && q.length > 0 && items.length > 0;

  return (
    <div
      className={cn(
        'absolute top-[calc(100%-4px)] left-0 w-full bg-white shadow-lg z-10 md:w-110 rounded-b-sm border border-black border-t-0',
        {
          hidden: !q,
        },
      )}
    >
      {loading && (
        <div className="py-4 flex justify-center">
          <Spinner />
        </div>
      )}

      {showEmpty && (
        <div className="py-3 px-4 text-sm text-gray-500">Nothing found</div>
      )}

      {showList && (
        <div className="max-h-60 overflow-y-auto">
          {items.map((item) => {
            const key = `igdb:${item.igdbId ?? item.id}`;
            const isHovered = hoveredKey === key;

            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(item)}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                  isHovered ? 'bg-pink-accent/5' : 'hover:bg-gray-50',
                )}
              >
                {item.coverThumbUrl ? (
                  <img
                    src={item.coverThumbUrl}
                    alt={item.title}
                    className={cn(
                      'w-7 h-10 rounded-md object-cover flex-shrink-0',
                      { 'size-7': item.type === 'project' },
                    )}
                  />
                ) : (
                  <div className="w-7 h-10 rounded-md bg-gray-200 flex-shrink-0" />
                )}

                <div className="min-w-0 flex-1">
                  <div
                    className={cn(
                      'font-medium line-clamp-1',
                      // isSelected ||
                      isHovered ? 'text-pink-accent' : 'text-gray-900',
                    )}
                  >
                    {item.title}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function HomeGamesSearch() {
  const router = useRouter();

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const [results, setResults] = useState<SearchGameItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<SearchGameItem | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  const isOpenSearchMobile = useBoolean(false);
  const isMobile = useResponsive('down', 'md');
  const shouldShowMobileCollapsed = isMobile && !isOpenSearchMobile.value;

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setHoveredKey(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setHoveredKey(null);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    const q = query.trim();
    if (!q) {
      setResults([]);
      setSelectedItem(null);
      setLoading(false);
      return;
    }

    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const { data, error } = await searchSocialGames(q, 20);
        if (error || !data) {
          console.error('HomeGamesSearch search error', error);
          setResults([]);
          return;
        }
        setResults(data.items ?? []);
      } catch (e) {
        console.error('HomeGamesSearch search exception', e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 900);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  const onClear = () => {
    setQuery('');
    setResults([]);
    setSelectedItem(null);
    setOpen(false);
    setHoveredKey(null);
  };

  const onSelect = async (item: SearchGameItem) => {
    setSelectedItem(item);
    setOpen(false);
    setHoveredKey(null);

    try {
      if (item.type === 'project') {
        router.push(`/?game=${item.slug}`);
      } else {
        const slug =
          item.source !== 'db'
            ? (await ensureIgdbGameInDb(item)).slug
            : (item.slug ?? '');
        if (!slug) return;

        router.push(`/?game=${encodeURIComponent(slug)}`);
      }
    } catch (e) {
      console.error('Failed to open game page', e);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative font-grotesk', {
        'w-[calc(50%+20px)]': !isOpenSearchMobile.value,
      })}
    >
      {shouldShowMobileCollapsed ? (
        <div className="flex md:hidden items-center justify-between">
          <Link href="/">
            <Image
              src="/icons/logoHero.png"
              alt="logo"
              width={40}
              height={40}
              priority
              className="object-contain"
            />
          </Link>
          <Icon
            name="magnifying-glass"
            className="size-6"
            onClick={isOpenSearchMobile.onTrue}
          />
        </div>
      ) : (
        <SearchBar
          query={query}
          setQuery={(v) => {
            setQuery(v);
            setOpen(true);
          }}
          onFocusOpen={() => setOpen(true)}
          onEscapeClose={() => {
            setOpen(false);
            setHoveredKey(null);
          }}
          isMobile={isMobile}
          onClear={onClear}
          onIconClick={isMobile ? isOpenSearchMobile.onFalse : undefined}
        />
      )}

      <DropdownResults
        open={open}
        loading={loading}
        query={query}
        items={results}
        hoveredKey={hoveredKey}
        setHoveredKey={setHoveredKey}
        selectedItem={selectedItem}
        onSelect={onSelect}
      />
    </div>
  );
}
